/**
 * External import parsers — Postman Collection v2.1 and Altair GraphQL.
 *
 * Both convert to SpuerhundExport so the existing importData() flow handles
 * merge, persistence, and store refresh.
 */

import type {
  SpuerhundExport,
  StoredServer,
  StoredQuery,
  RestMethod,
  RestContentType,
  RestParam,
} from "./persistence";

// ---------------------------------------------------------------------------
// Postman Collection v2.1 types
// ---------------------------------------------------------------------------

interface PostmanCollection {
  info: { name: string; schema?: string };
  item: PostmanItem[];
  auth?: PostmanAuth;
  variable?: PostmanVariable[];
}

interface PostmanItem {
  name: string;
  item?: PostmanItem[];
  request?: PostmanRequest;
}

interface PostmanRequest {
  method: string;
  header?: Array<{ key: string; value: string; disabled?: boolean }>;
  body?: PostmanBody;
  url: string | PostmanUrl;
  auth?: PostmanAuth;
}

interface PostmanUrl {
  raw: string;
  protocol?: string;
  host?: string[];
  port?: string;
  path?: string[];
}

interface PostmanBody {
  mode: string;
  graphql?: { query: string; variables?: string };
  raw?: string;
  options?: { raw?: { language?: string } };
}

interface PostmanAuth {
  type: string;
  bearer?: Array<{ key: string; value: string }>;
  apikey?: Array<{ key: string; value: string }>;
  basic?: Array<{ key: string; value: string }>;
}

interface PostmanVariable {
  key: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Altair GraphQL export types
// ---------------------------------------------------------------------------

interface AltairExport {
  version: number;
  type?: string;
  windows?: AltairWindow[];
  collections?: AltairCollection[];
}

interface AltairWindow {
  title?: string;
  windowName?: string;
  url: string;
  query: string;
  variables?: string;
  headers?: Array<{ key: string; value: string; enabled?: boolean }>;
}

interface AltairCollection {
  title?: string;
  queries: AltairWindow[];
}

// ---------------------------------------------------------------------------
// Shared result types
// ---------------------------------------------------------------------------

export interface ImportWarning {
  type: "env-variable" | "unsupported-auth" | "info";
  message: string;
}

export interface ImportSummary {
  source: "postman" | "altair";
  collectionName?: string;
  servers: Array<{ name: string; queryCount: number }>;
  totalQueries: number;
}

export interface ExternalImportResult {
  data: SpuerhundExport;
  warnings: ImportWarning[];
  summary: ImportSummary;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateId(): string {
  return crypto.randomUUID();
}

function timestamp(): string {
  return new Date().toISOString();
}

function extractBaseAndPath(urlStr: string): { baseUrl: string; path: string; params: RestParam[] } {
  try {
    const url = new URL(urlStr);
    return {
      baseUrl: `${url.protocol}//${url.host}`,
      path: url.pathname || "/graphql",
      params: [...url.searchParams.entries()].map(([key, value]) => ({ key, value })),
    };
  } catch {
    return { baseUrl: urlStr, path: "/graphql", params: [] };
  }
}

/** Scan any value tree for {{…}} Postman-style env variable references. */
function findEnvVars(obj: unknown): string[] {
  const vars = new Set<string>();
  const walk = (val: unknown) => {
    if (typeof val === "string") {
      for (const m of val.matchAll(/\{\{([^}]+)\}\}/g)) {
        if (m[1]) vars.add(m[1]);
      }
    } else if (Array.isArray(val)) {
      val.forEach(walk);
    } else if (val && typeof val === "object") {
      Object.values(val).forEach(walk);
    }
  };
  walk(obj);
  return [...vars].sort();
}

function mostCommon(arr: string[]): string {
  const counts = new Map<string, number>();
  for (const v of arr) counts.set(v, (counts.get(v) || 0) + 1);
  let best: string = arr[0] ?? "";
  let bestN = 0;
  for (const [v, n] of counts) {
    if (n > bestN) { best = v; bestN = n; }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Postman helpers
// ---------------------------------------------------------------------------

function resolvePostmanUrl(url: string | PostmanUrl): string {
  if (typeof url === "string") return url;
  if (url.raw) return url.raw;
  return [
    url.protocol ? `${url.protocol}://` : "https://",
    (url.host || []).join("."),
    url.port ? `:${url.port}` : "",
    "/",
    (url.path || []).join("/"),
  ].join("");
}

function flattenPostmanItems(
  items: PostmanItem[],
  prefix = "",
): Array<{ name: string; request: PostmanRequest }> {
  const result: Array<{ name: string; request: PostmanRequest }> = [];
  for (const item of items) {
    const label = prefix ? `${prefix} / ${item.name}` : item.name;
    if (item.request) result.push({ name: label, request: item.request });
    if (item.item) result.push(...flattenPostmanItems(item.item, label));
  }
  return result;
}

function mapPostmanAuth(
  auth: PostmanAuth | undefined,
  warnings: ImportWarning[],
  scopeLabel: string,
): { authType: StoredServer["authType"]; authConfig: string } {
  if (!auth || auth.type === "noauth") return { authType: "none", authConfig: "{}" };

  if (auth.type === "bearer") {
    const token = auth.bearer?.find((b) => b.key === "token")?.value ?? "";
    return { authType: "bearer", authConfig: JSON.stringify({ token }) };
  }

  if (auth.type === "apikey") {
    const headerName = auth.apikey?.find((b) => b.key === "key")?.value ?? "";
    const apiKey = auth.apikey?.find((b) => b.key === "value")?.value ?? "";
    const addTo = auth.apikey?.find((b) => b.key === "in")?.value ?? "header";
    if (addTo !== "header") {
      warnings.push({
        type: "unsupported-auth",
        message: `${scopeLabel}: API key auth in "${addTo}" is not imported automatically. Reconfigure it in the server auth settings after import.`,
      });
      return { authType: "none", authConfig: "{}" };
    }
    return { authType: "apikey", authConfig: JSON.stringify({ headerName, apiKey }) };
  }

  if (auth.type === "basic") {
    const username = auth.basic?.find((b) => b.key === "username")?.value ?? "";
    const password = auth.basic?.find((b) => b.key === "password")?.value ?? "";
    return {
      authType: "custom",
      authConfig: JSON.stringify({
        mode: "static",
        headers: {
          Authorization: `Basic ${btoa(`${username}:${password}`)}`,
        },
      }),
    };
  }

  warnings.push({
    type: "unsupported-auth",
    message: `${scopeLabel}: Postman auth type "${auth.type}" is not imported automatically. Reconfigure it in the server auth settings after import.`,
  });
  return { authType: "none", authConfig: "{}" };
}

// ---------------------------------------------------------------------------
// Postman parser
// ---------------------------------------------------------------------------

export function parsePostmanCollection(json: unknown): ExternalImportResult {
  const col = json as PostmanCollection;
  if (!col.info?.name || !Array.isArray(col.item)) {
    throw new Error("Invalid Postman Collection format");
  }

  const ts = timestamp();
  const warnings: ImportWarning[] = [];

  // Environment variable detection
  const envVars = findEnvVars(json);
  if (envVars.length > 0) {
    warnings.push({
      type: "env-variable",
      message: envVars.join(", "),
    });
  }

  // Collection variables (informational)
  if (col.variable?.length) {
    warnings.push({
      type: "info",
      message: `Collection variables: ${col.variable.map((v) => `${v.key}=${v.value}`).join(", ")}`,
    });
  }

  const flat = flattenPostmanItems(col.item);

  // Group by base URL
  const grouped = new Map<string, Array<{ name: string; request: PostmanRequest; path: string; params: RestParam[] }>>();
  for (const item of flat) {
    const rawUrl = resolvePostmanUrl(item.request.url);
    const { baseUrl, path, params } = extractBaseAndPath(rawUrl);
    if (!grouped.has(baseUrl)) grouped.set(baseUrl, []);
    grouped.get(baseUrl)!.push({ ...item, path, params });
  }

  const collectionAuth = mapPostmanAuth(col.auth, warnings, `Collection "${col.info.name}"`);
  const servers: StoredServer[] = [];
  const queries: StoredQuery[] = [];
  let serverSort = 0;

  for (const [baseUrl, items] of grouped) {
    const serverId = generateId();
    let serverName: string;

    // Primary GraphQL path (most common among graphql-mode requests)
    const gqlPaths = items.filter((i) => i.request.body?.mode === "graphql").map((i) => i.path);
    const graphqlPath = gqlPaths.length > 0 ? mostCommon(gqlPaths) : undefined;

    try {
      serverName = new URL(baseUrl).hostname;
    } catch {
      serverName = baseUrl;
    }

    // Auth: first request-level override, else collection-level
    const requestAuth = items.find((i) => i.request.auth)?.request.auth;
    const auth = requestAuth ? mapPostmanAuth(requestAuth, warnings, `Server "${serverName}"`) : collectionAuth;

    servers.push({
      id: serverId,
      name: serverName,
      baseUrl,
      graphqlPath,
      authType: auth.authType,
      authConfig: auth.authConfig,
      sortOrder: serverSort++,
      createdAt: ts,
      updatedAt: ts,
    });

    let qSort = 0;
    for (const item of items) {
      const isGraphQL = item.request.body?.mode === "graphql";
      const headers: Record<string, string> = {};
      for (const h of item.request.header ?? []) {
        if (!h.disabled) headers[h.key] = h.value;
      }

      if (isGraphQL) {
        queries.push({
          id: generateId(),
          serverId,
          name: item.name,
          queryText: item.request.body?.graphql?.query ?? "",
          variables: item.request.body?.graphql?.variables || "{}",
          headers: JSON.stringify(headers),
          requestType: "graphql",
          sortOrder: qSort++,
          createdAt: ts,
          updatedAt: ts,
        });
      } else {
        const method = (item.request.method || "GET").toUpperCase() as RestMethod;
        let bodyText = "";
        let contentType: RestContentType = "json";
        if (item.request.body?.mode === "raw") {
          bodyText = item.request.body.raw ?? "";
          const lang = item.request.body.options?.raw?.language;
          if (lang === "xml") contentType = "xml";
          else if (lang === "text") contentType = "text";
        }

        queries.push({
          id: generateId(),
          serverId,
          name: item.name,
          queryText: bodyText,
          variables: "{}",
          headers: JSON.stringify(headers),
          requestType: "rest",
          restMethod: method,
          restPath: item.path,
          restParams: item.params,
          restContentType: contentType,
          sortOrder: qSort++,
          createdAt: ts,
          updatedAt: ts,
        });
      }
    }
  }

  return {
    data: { version: 2, exportedAt: ts, servers, queries, schemaCache: [], history: [] },
    warnings,
    summary: {
      source: "postman",
      collectionName: col.info.name,
      servers: servers.map((s) => ({
        name: s.name,
        queryCount: queries.filter((q) => q.serverId === s.id).length,
      })),
      totalQueries: queries.length,
    },
  };
}

// ---------------------------------------------------------------------------
// Altair parser
// ---------------------------------------------------------------------------

export function parseAltairExport(json: unknown): ExternalImportResult {
  const alt = json as AltairExport;
  const ts = timestamp();
  const warnings: ImportWarning[] = [];

  // Collect all windows from both top-level and nested collections
  const allWindows: AltairWindow[] = [];
  if (alt.windows?.length) allWindows.push(...alt.windows);
  if (alt.collections?.length) {
    for (const c of alt.collections) {
      if (c.queries?.length) allWindows.push(...c.queries);
    }
  }

  if (allWindows.length === 0) {
    throw new Error("No queries found in Altair export");
  }

  // Group by base URL
  const grouped = new Map<string, AltairWindow[]>();
  for (const win of allWindows) {
    if (!win.url) continue;
    const { baseUrl } = extractBaseAndPath(win.url);
    if (!grouped.has(baseUrl)) grouped.set(baseUrl, []);
    grouped.get(baseUrl)!.push(win);
  }

  const servers: StoredServer[] = [];
  const queries: StoredQuery[] = [];
  let serverSort = 0;

  for (const [baseUrl, windows] of grouped) {
    const serverId = generateId();
    const firstUrl = windows[0]?.url ?? baseUrl;
    const { path } = extractBaseAndPath(firstUrl);

    let serverName: string;
    try {
      serverName = new URL(baseUrl).hostname;
    } catch {
      serverName = baseUrl;
    }

    // Detect bearer auth from headers
    const allHeaders = windows.flatMap((w) => w.headers ?? []);
    const authHeader = allHeaders.find((h) => h.key?.toLowerCase() === "authorization");
    let authType: StoredServer["authType"] = "none";
    let authConfig = "{}";
    if (authHeader?.value?.toLowerCase().startsWith("bearer ")) {
      authType = "bearer";
      authConfig = JSON.stringify({ token: authHeader.value.slice(7) });
    }

    servers.push({
      id: serverId,
      name: serverName,
      baseUrl,
      graphqlPath: path,
      authType,
      authConfig,
      sortOrder: serverSort++,
      createdAt: ts,
      updatedAt: ts,
    });

    let qSort = 0;
    for (const win of windows) {
      const headers: Record<string, string> = {};
      for (const h of win.headers ?? []) {
        if (h.enabled !== false && h.key?.toLowerCase() !== "authorization") {
          headers[h.key] = h.value;
        }
      }

      queries.push({
        id: generateId(),
        serverId,
        name: win.windowName || win.title || "Query",
        queryText: win.query || "",
        variables: win.variables || "{}",
        headers: JSON.stringify(headers),
        requestType: "graphql",
        sortOrder: qSort++,
        createdAt: ts,
        updatedAt: ts,
      });
    }
  }

  const collectionName = alt.collections?.[0]?.title ?? undefined;

  return {
    data: { version: 2, exportedAt: ts, servers, queries, schemaCache: [], history: [] },
    warnings,
    summary: {
      source: "altair",
      ...(collectionName ? { collectionName } : {}),
      servers: servers.map((s) => ({
        name: s.name,
        queryCount: queries.filter((q) => q.serverId === s.id).length,
      })),
      totalQueries: queries.length,
    },
  };
}

// ---------------------------------------------------------------------------
// Detection — auto-detect external format from parsed JSON
// ---------------------------------------------------------------------------

export function detectExternalFormat(
  json: unknown,
): "postman" | "altair" | null {
  if (!json || typeof json !== "object") return null;
  const obj = json as Record<string, unknown>;

  // Postman: has info.name + item array
  if (
    obj.info &&
    typeof obj.info === "object" &&
    "name" in (obj.info as Record<string, unknown>) &&
    Array.isArray(obj.item)
  ) {
    return "postman";
  }

  // Altair: has windows or collections array
  if (Array.isArray(obj.windows) || Array.isArray(obj.collections)) {
    return "altair";
  }

  return null;
}
